import { NavLink, useLocation } from 'react-router-dom'
import { useRef, useLayoutEffect, useState, forwardRef } from 'react'
import { cn } from '@/utils/cn'
import { 
  Home, 
  FolderOpen,
  Gift,
  CreditCard, 
  Video, 
  Image, 
  Film, 
  Crown,
  HelpCircle, 
  LogIn, 
  UserPlus 
} from 'lucide-react'
import { useRedirect } from '@/contexts/redirect-context'

interface NavItem {
  icon: React.ElementType
  label: string
  href: string
  isSpecial?: boolean
  isExternal?: boolean
  requiresPlan?: boolean // Show crown icon for premium features
}

const mainNavItems: NavItem[] = [
  { icon: Home, label: 'Home', href: '/' },
  { icon: FolderOpen, label: 'Assets', href: '/assets', isExternal: true },
  { icon: Gift, label: 'Daily Rewards', href: '/rewards', isExternal: true, requiresPlan: true },
  { icon: CreditCard, label: 'Upgrade', href: '/billing', isSpecial: true, isExternal: true },
]

const createNavItems: NavItem[] = [
  { icon: Video, label: 'AI Video', href: '/video' },
  { icon: Image, label: 'AI Images', href: '/images', isExternal: true },
]

const utilitiesNavItems: NavItem[] = [
  { icon: Film, label: 'Extend Video', href: '/extend-video', isExternal: true, requiresPlan: true },
]

export function Sidebar() {
  return (
    <aside className="w-64 h-full glass border-y-0 border-l-0 flex flex-col overflow-hidden">
      {/* Scrollable nav section */}
      <div className="flex-1 min-h-0 pt-6 pb-4 overflow-y-auto overscroll-none">
        <SidebarNav />
      </div>
      {/* Fixed profile section at bottom */}
      <div className="shrink-0">
        <ProfileSection />
      </div>
    </aside>
  )
}

function SidebarNav() {
  const location = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const itemRefs = useRef<Map<string, HTMLAnchorElement | HTMLButtonElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 })

  // Combine all items for indicator tracking
  const allItems = [...mainNavItems, ...createNavItems, ...utilitiesNavItems]

  // Find active item based on current path
  const activeHref = allItems.find((item) =>
    item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
  )?.href

  useLayoutEffect(() => {
    if (!activeHref || !navRef.current) {
      setIndicatorStyle((s) => ({ ...s, opacity: 0 }))
      return
    }

    const rafId = requestAnimationFrame(() => {
      const activeEl = itemRefs.current.get(activeHref)
      if (activeEl && navRef.current) {
        const navRect = navRef.current.getBoundingClientRect()
        const activeRect = activeEl.getBoundingClientRect()
        setIndicatorStyle({
          top: activeRect.top - navRect.top,
          height: activeRect.height,
          opacity: 1,
        })
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [activeHref])

  return (
    <nav ref={navRef} className="px-3 relative">
      {/* Sliding glass indicator */}
      <div
        className={cn(
          'absolute left-3 right-3 rounded-lg bg-white/[0.05] border border-white/[0.1] transition-all duration-300 ease-out pointer-events-none',
          !activeHref && 'opacity-0'
        )}
        style={{
          top: indicatorStyle.top,
          height: indicatorStyle.height,
          opacity: indicatorStyle.opacity,
        }}
      />

      {/* Main nav items */}
      <div className="space-y-1.5">
        {mainNavItems.map((item) => (
          <NavButton
            key={item.href}
            item={item}
            ref={(el) => {
              if (el) itemRefs.current.set(item.href, el)
              else itemRefs.current.delete(item.href)
            }}
          />
        ))}
      </div>

      {/* Create section */}
      <div className="mt-6">
        <div className="px-4 pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          Create
        </div>
        <div className="space-y-1.5">
          {createNavItems.map((item) => (
            <NavButton
              key={item.href}
              item={item}
              ref={(el) => {
                if (el) itemRefs.current.set(item.href, el)
                else itemRefs.current.delete(item.href)
              }}
            />
          ))}
        </div>
      </div>

      {/* Utilities section */}
      <div className="mt-6">
        <div className="px-4 pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          Utilities
        </div>
        <div className="space-y-1.5">
          {utilitiesNavItems.map((item) => (
            <NavButton
              key={item.href}
              item={item}
              ref={(el) => {
                if (el) itemRefs.current.set(item.href, el)
                else itemRefs.current.delete(item.href)
              }}
            />
          ))}
        </div>
      </div>
    </nav>
  )
}

function ProfileSection() {
  const { openRedirect } = useRedirect()

  const handleSignIn = () => {
    openRedirect()
  }

  const handleJoinFree = () => {
    openRedirect()
  }

  const handleSupport = () => {
    openRedirect('/support')
  }

  return (
    <div className="p-3 border-t border-white/[0.08]">
      {/* Support link */}
      <button
        onClick={handleSupport}
        className="flex items-center gap-3 w-full px-3 py-2 mb-3 rounded-md text-[13px] text-[#888] hover:text-white hover:bg-white/[0.04] transition-colors"
      >
        <HelpCircle className="size-4" strokeWidth={1.5} />
        Support
      </button>

      <div className="space-y-2">
        <button 
          onClick={handleSignIn}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-white/[0.05] border border-white/[0.1] hover:bg-white/[0.08] transition-colors"
        >
          <LogIn className="size-4" />
          Log In
        </button>
        <button 
          onClick={handleJoinFree}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-black bg-white hover:bg-white/90 transition-colors"
        >
          <UserPlus className="size-4" />
          Join Free
        </button>
      </div>
    </div>
  )
}

interface NavButtonProps {
  item: NavItem
}

const NavButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, NavButtonProps>(({ item }, ref) => {
  const Icon = item.icon
  const location = useLocation()
  const { openRedirect } = useRedirect()
  const isActive = item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)

  // External links (redirect to pixelmaker.ai with modal)
  if (item.isExternal) {
    const handleClick = () => {
      const path = item.href === '/billing' ? '/billing' : item.href
      openRedirect(path)
    }

    if (item.isSpecial) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={handleClick}
          className="relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)] hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
        >
          <Icon className="size-[18px]" />
          {item.label}
        </button>
      )
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={handleClick}
        className={cn(
          'relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
          'text-[#666] hover:text-white hover:bg-white/[0.04]'
        )}
      >
        <Icon className="size-[18px]" />
        <span className="flex-1 text-left">{item.label}</span>
        {item.requiresPlan && (
          <Crown className="size-4 text-amber-400" />
        )}
      </button>
    )
  }

  // Internal links (stay on cloak site)
  if (item.isSpecial) {
    return (
      <NavLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={item.href}
        end={item.href === '/'}
        className="relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)] hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
      >
        <Icon className="size-[18px]" />
        {item.label}
      </NavLink>
    )
  }

  return (
    <NavLink
      ref={ref as React.Ref<HTMLAnchorElement>}
      to={item.href}
      end={item.href === '/'}
      className={cn(
        'relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
        isActive ? 'text-white' : 'text-[#666] hover:text-white hover:bg-white/[0.04]'
      )}
    >
      <Icon className="size-[18px]" />
      <span className="flex-1">{item.label}</span>
      {item.requiresPlan && (
        <Crown className="size-4 text-amber-400" />
      )}
    </NavLink>
  )
})

NavButton.displayName = 'NavButton'
